import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Plus, Trash2, Users as UsersIcon, Check, X,
  ChevronDown, ChevronRight, Sparkles, Eye, Settings as SettingsIcon, DollarSign, FileEdit,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import CustomRolesAuditLog from "@/components/CustomRolesAuditLog";

// Iter-201 — Quick templates that pre-check a coherent permission bundle.
// Each template is purely client-side; saving still goes through /custom-roles.
const TEMPLATES = [
  {
    id: "read_only",
    label: "Lecture seule",
    icon: Eye,
    desc: "Vue d'ensemble + AR aging + lecture des missions. Aucune action de modification.",
    perms: ["view_global_overview", "view_ar_aging", "view_all_missions", "view_team_timesheet", "view_purchase_orders"],
  },
  {
    id: "manager_plus",
    label: "Manager standard +",
    icon: Sparkles,
    desc: "Manager classique + simulation de mission + chatbot.",
    perms: ["timesheet", "manage_missions", "manage_own_clients", "validate_cra", "unlock_cra", "export_cra", "view_team_timesheet", "view_purchase_orders", "run_simulations", "use_chatbot", "submit_expenses"],
  },
  {
    id: "finance",
    label: "Gestionnaire finance",
    icon: DollarSign,
    desc: "Pilotage facturation, AR aging, relances, déclarations de paiement, NDF.",
    perms: ["invoicing", "auto_invoice", "view_ar_aging", "manage_invoice_reminders", "validate_payment_declarations", "validate_expenses", "view_all_expenses", "manage_cabinet_expenses", "view_all_missions", "view_purchase_orders"],
  },
  {
    id: "expenses_submitter",
    label: "Soumetteur NDF",
    icon: FileEdit,
    desc: "Permet à un collaborateur de soumettre ses notes de frais via le module dédié.",
    perms: ["submit_expenses"],
  },
];

export default function CustomRoles() {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState({ catalog: {}, all_permissions: [] });
  const [editing, setEditing] = useState(null); // role being edited
  const [assignRole, setAssignRole] = useState(null); // role being assigned
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    const [r, u, c] = await Promise.all([
      api.get("/custom-roles"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/permissions/catalog"),
    ]);
    setRoles(r.data); setUsers(u.data); setCatalog(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ name: "", description: "", permissions: [] });
    // Open the first module by default for discoverability.
    const firstMod = Object.keys(catalog.catalog || {})[0];
    setExpanded(firstMod ? { [firstMod]: true } : {});
  };
  const openEdit = (r) => {
    setEditing({ ...r, permissions: [...(r.permissions || [])] });
    setExpanded({});
  };
  const togglePerm = (p) => {
    setEditing((e) => ({ ...e, permissions: e.permissions.includes(p) ? e.permissions.filter((x) => x !== p) : [...e.permissions, p] }));
  };
  const applyTemplate = (tpl) => {
    setEditing((e) => ({
      ...e,
      permissions: Array.from(new Set([...(e.permissions || []), ...tpl.perms])),
    }));
    // Auto-expand all modules so the user sees what got checked.
    const all = {};
    Object.keys(catalog.catalog || {}).forEach((m) => { all[m] = true; });
    setExpanded(all);
    toast.success(`Template "${tpl.label}" appliqué (${tpl.perms.length} perms)`);
  };
  const clearAll = () => setEditing((e) => ({ ...e, permissions: [] }));

  // Iter-202 — Robust error extraction so the user always sees WHY the save failed,
  // including HTTP status, network errors, and stringified detail objects.
  // Previously a network/timeout error rendered a useless "Error" toast.
  const extractErrorMessage = (e, fallback) => {
    // Network error (no response at all) — most common cause of empty toast.
    if (e?.code === "ERR_NETWORK" || !e?.response) {
      // eslint-disable-next-line no-console
      console.error("[CustomRoles] Network error:", e);
      return `Erreur réseau — vérifiez votre connexion. (${e?.message || "unknown"})`;
    }
    const status = e.response.status;
    const data = e.response.data;
    let detail = data?.detail;
    // detail can be: string, array (Pydantic 422 — already coerced by interceptor),
    // object (rare custom errors), or null/undefined.
    if (detail && typeof detail === "object") {
      try { detail = JSON.stringify(detail); } catch (_e) { detail = null; }
    }
    if (typeof detail === "string" && detail.trim()) {
      return `${detail} (HTTP ${status})`;
    }
    // eslint-disable-next-line no-console
    console.error("[CustomRoles] Save error w/o detail:", { status, data, error: e });
    return `${fallback} — HTTP ${status}`;
  };

  const save = async () => {
    try {
      if (!editing.name.trim()) return toast.error("Le nom du rôle est requis.");
      if (editing.role_id) {
        await api.patch(`/custom-roles/${editing.role_id}`, { name: editing.name, description: editing.description, permissions: editing.permissions });
        toast.success("Rôle mis à jour");
      } else {
        await api.post("/custom-roles", { name: editing.name, description: editing.description, permissions: editing.permissions });
        toast.success("Rôle créé");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(extractErrorMessage(e, "Échec de la sauvegarde du rôle")); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"? It will be unassigned from all users.`)) return;
    try {
      await api.delete(`/custom-roles/${r.role_id}`);
      toast.success("Rôle supprimé");
      load();
    } catch (e) { toast.error(extractErrorMessage(e, "Échec de la suppression")); }
  };

  const assign = async (uid, rid, add) => {
    try {
      await api.post(`/users/${uid}/custom-roles/${add ? "assign" : "unassign"}`, { role_id: rid });
      toast.success(add ? "Assigné" : "Désassigné");
      load();
    } catch (e) { toast.error(extractErrorMessage(e, "Échec de l'assignation")); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="custom-roles-page">
      <div className="flex flex-wrap gap-3 items-baseline justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Admin · Permissions</div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Custom roles</h1>
          <p className="text-zinc-500 mt-3 max-w-2xl">Bundle granular permissions and assign them to specific users on top of their system role. Custom roles are <strong>additive</strong> — they only grant, never revoke.</p>
        </div>
        <button onClick={openNew} data-testid="new-role-btn" className="inline-flex items-center gap-2 px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
          <Plus size={14} /> New role
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {roles.map((r) => {
          const assigned = users.filter((u) => (u.custom_role_ids || []).includes(r.role_id));
          return (
            <div key={r.role_id} className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`role-card-${r.role_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-zinc-700" />
                    <div className="font-heading font-bold text-base">{r.name}</div>
                  </div>
                  {r.description && <div className="text-xs text-zinc-500 mt-1">{r.description}</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(r)} data-testid={`role-edit-${r.role_id}`} className="text-xs px-3 h-8 border border-zinc-200 rounded hover:bg-zinc-50">Edit</button>
                  <button onClick={() => remove(r)} data-testid={`role-delete-${r.role_id}`} className="p-2 text-zinc-500 hover:text-red-600 rounded hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-4">
                {r.permissions.map((p) => (
                  <span key={p} className="text-[10px] px-2 py-0.5 bg-zinc-100 text-zinc-800 rounded-full font-mono">{p}</span>
                ))}
                {r.permissions.length === 0 && <span className="text-xs text-zinc-400 italic">No permissions yet</span>}
              </div>
              <div className="border-t border-zinc-100 pt-3 flex items-center justify-between">
                <div className="text-xs text-zinc-500 flex items-center gap-1"><UsersIcon size={12} /> {assigned.length} assignee(s)</div>
                <button onClick={() => setAssignRole(r)} data-testid={`role-assign-${r.role_id}`} className="text-xs px-3 h-8 bg-zinc-950 text-white rounded hover:bg-zinc-800">Manage users</button>
              </div>
              {assigned.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {assigned.map((u) => (
                    <span key={u.user_id} className="inline-flex items-center gap-1 text-xs bg-zinc-50 border border-zinc-200 px-2 py-1 rounded-full">{u.name}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {roles.length === 0 && (
          <div className="col-span-full text-zinc-500 text-sm text-center py-16 border border-dashed border-zinc-200 rounded-lg">
            No custom roles yet. Create one to grant bundles of permissions to specific users.
          </div>
        )}
      </div>

      {/* Iter-202b — Audit log : qui a touché à quoi et quand */}
      <CustomRolesAuditLog canManage={true} />

      {/* Edit / Create Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-auto max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.role_id ? "Edit role" : "Create custom role"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nom du rôle</Label>
                  <Input data-testid="role-form-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="ex: Finance Viewer" />
                </div>
                <div>
                  <Label>Compteur</Label>
                  <div className="flex items-center h-10 px-3 border border-zinc-200 rounded-md bg-zinc-50 text-sm">
                    <span className="font-semibold text-zinc-900">{editing.permissions.length}</span>
                    <span className="text-zinc-500 ml-1">permission(s) sélectionnée(s) sur {catalog.all_permissions?.length || 0}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="role-form-desc" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="À quoi sert ce rôle..." />
              </div>

              {/* Iter-201 — Templates rapides */}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-2">Templates rapides</div>
                <div className="flex flex-wrap gap-2">
                  {TEMPLATES.map((tpl) => {
                    const Icon = tpl.icon;
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => applyTemplate(tpl)}
                        data-testid={`role-tpl-${tpl.id}`}
                        title={tpl.desc}
                        className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-zinc-300 bg-white rounded-md hover:bg-zinc-50 hover:border-zinc-400"
                      >
                        <Icon size={12} /> {tpl.label}
                        <span className="text-[9px] font-mono text-zinc-400">+{tpl.perms.length}</span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={clearAll}
                    data-testid="role-clear-all"
                    className="inline-flex items-center gap-1.5 px-3 h-8 text-xs text-zinc-500 hover:text-rose-600"
                  >
                    <X size={12} /> Tout décocher
                  </button>
                </div>
              </div>

              {/* Permissions grouped by module */}
              <div>
                <Label>Permissions</Label>
                <div className="mt-2 space-y-2">
                  {Object.entries(catalog.catalog || {}).map(([modName, perms]) => {
                    const isOpen = !!expanded[modName];
                    const grantedCount = perms.filter((p) => editing.permissions.includes(p.key)).length;
                    return (
                      <div key={modName} className="border border-zinc-200 rounded-md overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpanded((e) => ({ ...e, [modName]: !e[modName] }))}
                          data-testid={`role-form-mod-toggle-${modName}`}
                          className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-zinc-50 text-left bg-zinc-50/50"
                        >
                          {isOpen ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronRight size={14} className="text-zinc-500" />}
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-zinc-900">{modName}</div>
                          </div>
                          <div className={`text-[10px] font-mono px-2 py-0.5 rounded ${grantedCount === 0 ? "bg-zinc-100 text-zinc-500" : grantedCount === perms.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            {grantedCount}/{perms.length}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="divide-y divide-zinc-100">
                            {perms.map((p) => {
                              const checked = editing.permissions.includes(p.key);
                              return (
                                <label
                                  key={p.key}
                                  className="flex items-start gap-3 px-3 py-2 hover:bg-zinc-50 cursor-pointer"
                                  data-testid={`role-form-perm-${p.key}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => togglePerm(p.key)}
                                    className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-zinc-950 focus:ring-zinc-950"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-zinc-900">{p.label}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5 leading-snug">{p.desc}</div>
                                    <div className="text-[10px] text-zinc-400 font-mono mt-0.5">{p.key}</div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={save} data-testid="role-form-save" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md inline-flex items-center gap-2"><Check size={14} /> Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={!!assignRole} onOpenChange={(o) => !o && setAssignRole(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Assign "{assignRole?.name}"</DialogTitle></DialogHeader>
          <div className="py-2 max-h-80 overflow-y-auto space-y-1">
            {users.map((u) => {
              const has = (u.custom_role_ids || []).includes(assignRole?.role_id);
              return (
                <div key={u.user_id} className="flex items-center justify-between px-3 py-2 rounded hover:bg-zinc-50" data-testid={`role-assign-user-${u.user_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {u.picture ? <img src={u.picture} className="w-7 h-7 rounded-full" alt="" referrerPolicy="no-referrer" /> : <div className="w-7 h-7 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-[10px] font-medium uppercase">{u.name?.slice(0,2)}</div>}
                    <div className="min-w-0">
                      <div className="text-sm truncate">{u.name}</div>
                      <div className="text-[10px] text-zinc-500 font-mono uppercase">{u.role}</div>
                    </div>
                  </div>
                  <button onClick={() => assign(u.user_id, assignRole.role_id, !has)} className={`text-xs px-3 h-8 rounded inline-flex items-center gap-1 ${has ? "bg-zinc-950 text-white" : "border border-zinc-200 hover:bg-zinc-50"}`}>
                    {has ? (<><Check size={12} /> Assigned</>) : (<><Plus size={12} /> Assign</>)}
                  </button>
                </div>
              );
            })}
            {users.length === 0 && <div className="text-sm text-zinc-500 text-center py-8">No users.</div>}
          </div>
          <DialogFooter>
            <button onClick={() => setAssignRole(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
